import { DestroyRef, Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { setUserId } from 'firebase/analytics';
import { Auth, authState } from '@fido/firebase/auth';
import { Analytics } from './tokens';

/**
 * Subscribes to auth state and forwards `user.uid` to Firebase Analytics's
 * `setUserId`. Activated by registering it as a class provider:
 *
 *   provideAnalytics(...),
 *   UserTrackingService,
 *   provideEnvironmentInitializer(() => inject(Analytics)),
 *
 * Ported from `@angular/fire/analytics`. The `initialized` Promise resolves
 * after the first `authState` emission so that `ScreenTrackingService` can
 * sequence `screen_view` events behind it (matching upstream behavior).
 */
@Injectable({ providedIn: 'root' })
export class UserTrackingService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly auth = inject(Auth, { optional: true });
  private readonly analytics = inject(Analytics, { optional: true });
  private readonly destroyRef = inject(DestroyRef);

  readonly initialized: Promise<void>;

  constructor() {
    let resolveInitialized: () => void = () => undefined;
    this.initialized = new Promise<void>((resolve) => {
      resolveInitialized = resolve;
    });

    if (!isPlatformBrowser(this.platformId) || !this.auth || !this.analytics) {
      resolveInitialized();
      return;
    }

    const analytics = this.analytics;
    authState(this.auth)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((user) => {
        setUserId(analytics, user?.uid ?? null);
        resolveInitialized();
      });
  }
}
